'use client'

/**
 * Shared restricted-Markdown renderer for chat surfaces — the web widget, the
 * inbox bubble, and the agent test chat all render outbound text through it.
 *
 * Security: no `dangerouslySetInnerHTML`. `rehype-sanitize` is the sole layer
 * enforcing tag and URL-scheme allowlists. The custom `a` renderer only hardens
 * `target` / `rel` — by the time it runs, unsafe schemes (including bare
 * `remark-gfm` autolinks) have already been stripped by the schema.
 */

import { Children, type ComponentProps } from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeSanitize, {
  defaultSchema,
  // `Options` is rehype-sanitize's re-export of hast-util-sanitize's `Schema`.
  type Options as SanitizeSchema,
} from 'rehype-sanitize'

/**
 * Sanitize schema for chat Markdown. Single source of truth — exported for
 * tests and shared by every surface that renders agent replies.
 *
 * Derived from `defaultSchema` so GFM output stays compatible, then tightened:
 * - `tagNames` reduced to a minimal semantic set.
 * - `attributes.a` restricted to `href` / `title`.
 * - `protocols.href` restricted to `http` / `https` / `mailto`. This is the
 *   ONLY place URL-scheme enforcement happens — it applies uniformly to both
 *   markdown-syntax links and `remark-gfm` autolinks of bare URLs in text.
 */
// `satisfies SanitizeSchema` — catches typos in keys like `protocols.hreff`
// or `attribuets.a` at compile time (a typo would silently disable
// sanitization). Uses `satisfies` instead of a type annotation to preserve
// the literal type of `tagNames` so the schema-invariant tests can compare
// with `.toEqual([...])`.
export const CHAT_MD_SANITIZE_SCHEMA = {
  ...defaultSchema,
  tagNames: [
    'p',
    'br',
    'strong',
    'em',
    'del',
    'code',
    'pre',
    'ul',
    'ol',
    'li',
    'a',
    'blockquote',
  ],
  attributes: {
    ...defaultSchema.attributes,
    a: ['href', 'title'],
  },
  protocols: {
    ...defaultSchema.protocols,
    href: ['http', 'https', 'mailto'],
  },
} satisfies SanitizeSchema

const MD_COMPONENTS: Components = { a: MarkdownLink }
const COMPACT_MD_COMPONENTS: Components = {
  a: props => <MarkdownLink {...props} hideLinkProtocol />,
}

/**
 * Bare renderer — emits no wrapper element on purpose. The widget's global CSS
 * targets direct children (`.widget-message > p`), which any wrapper would
 * break silently. Dashboard surfaces use `ChatMarkdownBody` instead.
 */
export function MarkdownMessage({ text, hideLinkProtocol = false }: { text: string; hideLinkProtocol?: boolean }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[[rehypeSanitize, CHAT_MD_SANITIZE_SCHEMA]]}
      components={hideLinkProtocol ? COMPACT_MD_COMPONENTS : MD_COMPONENTS}
    >
      {text}
    </ReactMarkdown>
  )
}

/**
 * Dashboard wrapper — the prose styles for Markdown chat bodies, defined once.
 * Arbitrary variants rather than a typography plugin (this project has none).
 * Tailwind Preflight strips list markers, so `ul`/`ol` restate them; links
 * inherit their colour because bubbles sit on blue/emerald/white backgrounds
 * where a fixed link colour is unreadable. Deliberately no `whitespace-pre-wrap`
 * — source newlines between block elements would become visible blank lines.
 */
export function ChatMarkdownBody({ text }: { text: string }) {
  return (
    <div
      className={[
        '[&_p]:m-0 [&_p+p]:mt-2',
        '[&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5',
        '[&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5',
        '[&_li]:my-0.5',
        '[&_blockquote]:my-1 [&_blockquote]:border-l-2 [&_blockquote]:border-current [&_blockquote]:pl-2 [&_blockquote]:opacity-80',
        '[&_code]:rounded [&_code]:bg-black/10 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[0.9em] [&_code]:break-words dark:[&_code]:bg-white/10',
        '[&_pre]:my-1.5 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-black/10 [&_pre]:p-2 [&_pre]:text-xs [&_pre]:whitespace-pre-wrap dark:[&_pre]:bg-white/10',
        '[&_pre_code]:bg-transparent [&_pre_code]:p-0',
        '[&_a]:text-inherit [&_a]:underline [&_a]:underline-offset-2 [&_a]:break-all',
      ].join(' ')}
    >
      <MarkdownMessage text={text} />
    </div>
  )
}

function MarkdownLink({ children, href, title, hideLinkProtocol = false }: ComponentProps<'a'> & { hideLinkProtocol?: boolean }) {
  const parts = Children.toArray(children)
  let label = children
  if (hideLinkProtocol && parts.length === 1 && typeof parts[0] === 'string') {
    label = parts[0].replace(/^https?:\/\//i, '')
  }

  return (
    <a href={href} title={title} target="_blank" rel="noopener noreferrer nofollow ugc">
      {label}
    </a>
  )
}
